import { useCallback, useEffect, useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import type { AdminGeofence } from './types'
import { createGeofence, deleteGeofence, fetchGeofences, updateGeofence } from './useAdminApi'
import { SettingsFormModal } from './SettingsFormModal'
import { AdminTable, Banner, Btn, Field, SectionHeader, Td, Th, inputCls } from './ui'

const FENCE_TYPES = ['plant', 'yard', 'depot', 'gate', 'queue', 'toll', 'ghat', 'rest', 'bridge', 'congestion', 'activity', 'zone']

const EMPTY: Partial<AdminGeofence> = {
  geofence_id: '', name: '', type: 'yard', hub_code: '', polygon: [], tat_minutes: null,
}

export function GeofencesSection({ configured }: { configured: boolean }) {
  const [rows, setRows] = useState<AdminGeofence[]>([])
  const [source, setSource] = useState('files')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'error'; msg: string } | null>(null)
  const [editing, setEditing] = useState<Partial<AdminGeofence> | null>(null)
  const [polygonText, setPolygonText] = useState('[]')
  const [isNew, setIsNew] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchGeofences()
      setRows(data.geofences)
      setSource(data.source)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load geofences')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const openNew = () => {
    setEditing({ ...EMPTY })
    setPolygonText('[]')
    setIsNew(true)
  }

  const openEdit = (g: AdminGeofence) => {
    setEditing({ ...g })
    setPolygonText(JSON.stringify(g.polygon ?? [], null, 2))
    setIsNew(false)
  }

  const save = async () => {
    if (!editing?.geofence_id?.trim() || !editing.type) {
      setBanner({ tone: 'error', msg: 'Geofence ID and type are required' })
      return
    }
    let polygon: [number, number][]
    try {
      polygon = JSON.parse(polygonText) as [number, number][]
      if (!Array.isArray(polygon) || polygon.length < 3) throw new Error('need 3+ points')
    } catch {
      setBanner({ tone: 'error', msg: 'Polygon must be valid JSON: [[lat,lon], ...] with 3+ points' })
      return
    }
    const payload = { ...editing, polygon }
    try {
      if (isNew) await createGeofence(payload)
      else await updateGeofence(editing.geofence_id, payload)
      setBanner({ tone: 'ok', msg: isNew ? 'Geofence created' : 'Geofence updated' })
      setEditing(null)
      await load()
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Save failed' })
    }
  }

  const remove = async (id: string) => {
    if (!configured) {
      setBanner({ tone: 'error', msg: 'Delete requires the reporting database — file-backed geofences are read-only' })
      return
    }
    if (!window.confirm(`Delete geofence ${id}?`)) return
    try {
      await deleteGeofence(id)
      setBanner({ tone: 'ok', msg: 'Geofence deleted' })
      await load()
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Delete failed' })
    }
  }

  const readOnly = source === 'files' && !configured

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Geofences"
        subtitle={`Corridor fence definitions${readOnly ? ' — read-only' : ''}.`}
        action={readOnly ? undefined : (
          <Btn onClick={openNew}><span className="inline-flex items-center gap-1"><Plus size={14} /> Add geofence</span></Btn>
        )}
      />
      {readOnly ? (
        <Banner tone="error">
          Showing the built-in fence set. Connect the reporting database to create or edit fences here.
        </Banner>
      ) : null}
      {banner ? <Banner tone={banner.tone} onDismiss={() => setBanner(null)}>{banner.msg}</Banner> : null}
      {error ? <Banner tone="error">{error}</Banner> : null}
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <AdminTable>
          <thead>
            <tr>
              <Th>ID</Th>
              <Th>Name</Th>
              <Th>Type</Th>
              <Th>Hub</Th>
              <Th>TAT (min)</Th>
              <Th>Points</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((g) => (
              <tr key={g.geofence_id} className="hover:bg-slate-50/80">
                <Td mono className="max-w-[160px] truncate">{g.geofence_id}</Td>
                <Td className="max-w-[180px] truncate">{g.name ?? '—'}</Td>
                <Td>{g.type}</Td>
                <Td mono>{g.hub_code ?? '—'}</Td>
                <Td mono>{g.tat_minutes ?? '—'}</Td>
                <Td mono>{g.polygon?.length ?? 0}</Td>
                <Td>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => openEdit(g)} className="rounded p-1 text-slate-500 hover:bg-slate-100" title="Edit">
                      <Pencil size={14} />
                    </button>
                    {!readOnly ? (
                      <button type="button" onClick={() => void remove(g.geofence_id)} className="rounded p-1 text-rose-500 hover:bg-rose-50" title="Delete">
                        <Trash2 size={14} />
                      </button>
                    ) : null}
                  </div>
                </Td>
              </tr>
            ))}
            {!rows.length ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-400">No geofences</td></tr>
            ) : null}
          </tbody>
        </AdminTable>
      )}

      <SettingsFormModal
        open={editing !== null}
        title={readOnly ? (editing?.geofence_id ?? 'Geofence') : isNew ? 'New geofence' : `Edit ${editing?.geofence_id ?? ''}`}
        onClose={() => setEditing(null)}
        onSave={readOnly ? undefined : () => void save()}
        readOnly={readOnly}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Geofence ID">
            <input className={inputCls} value={editing?.geofence_id ?? ''} disabled={!isNew || readOnly}
              onChange={(e) => setEditing({ ...editing!, geofence_id: e.target.value })} />
          </Field>
          <Field label="Name">
            <input className={inputCls} value={editing?.name ?? ''} disabled={readOnly}
              onChange={(e) => setEditing({ ...editing!, name: e.target.value })} />
          </Field>
          <Field label="Type">
            <select className={inputCls} value={editing?.type ?? 'yard'} disabled={readOnly}
              onChange={(e) => setEditing({ ...editing!, type: e.target.value })}>
              {FENCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Hub code">
            <input className={inputCls} value={editing?.hub_code ?? ''} disabled={readOnly}
              onChange={(e) => setEditing({ ...editing!, hub_code: e.target.value })} />
          </Field>
          <Field label="TAT minutes">
            <input className={inputCls} type="number" value={editing?.tat_minutes ?? ''} disabled={readOnly}
              onChange={(e) => setEditing({ ...editing!, tat_minutes: e.target.value ? Number(e.target.value) : null })} />
          </Field>
          <Field label="Speed limit (KMPH)">
            <input className={inputCls} type="number" value={editing?.speed_limit_kph ?? ''} disabled={readOnly}
              onChange={(e) => setEditing({ ...editing!, speed_limit_kph: e.target.value ? Number(e.target.value) : null })} />
          </Field>
        </div>
        <Field label="Polygon JSON [[lat, lon], ...]" className="mt-3">
          <textarea className={`${inputCls} min-h-[120px] font-mono text-xs`} value={polygonText}
            disabled={readOnly} onChange={(e) => setPolygonText(e.target.value)} />
        </Field>
      </SettingsFormModal>
    </div>
  )
}
